import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import classNames from "classnames";
import { Page, Synonym } from "@/model";
import synonymRepository from "@/repository/synonymRepository";
import { useDeleteSynonyms } from "@/query/synonym";
import { editingSynonymAtom } from "@/atom/editing";
import { useAgentId, useDialog, useSelection } from "@/hook";
import { IndeterminateCheckbox } from "@/component/input";
import HighlightText from "@/component/HighlightText";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { errorToast, successToast } from "@/component/Toast";
import SelectionActionBar from "@/component/SelectionActionBar";
import SortButton from "@/component/SortBurtton";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { SynonymPageParam } from "../SynonymView";


interface SynonymTableProps {
    page?: Page<Synonym>;
    pageParam: SynonymPageParam;
    onClickSortHead: (order: "asc" | "desc") => void;
}

const SynonymTable: React.FC<SynonymTableProps> = (props) => {
    const { page, pageParam, onClickSortHead } = props;
    
    const intl = useIntl();
    const agentId = useAgentId();
    const dialog = useDialog();

    const pageIds = useMemo(() => page?.content.map(synonym => synonym.id) ?? [], [page?.content]);

    const { selectionCount, isAllSelected, pageSelectionStatus, selectedIds, markOne, markPage, markAll, clearSelection } = useSelection({
        targetIds: pageIds,
        total: page?.total ?? 0
    });

    const [editingSynonym, setEditingSynonym] = useAtom(editingSynonymAtom);
    const { mutateAsync: deleteSynonyms } = useDeleteSynonyms(agentId);

    const deleteByIds = async (ids: string[]) => {
        await deleteSynonyms({ ids: ids })
            .then(() => {
                if (editingSynonym && ids.includes(editingSynonym.id)) {
                    setEditingSynonym(undefined);
                }
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const deleteAll = async () => {
        await deleteSynonyms({ keyword: pageParam.searchKeyword })
            .then(() => {
                setEditingSynonym(undefined);
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const onClickTableRowDeleteBtn = (id: string) => {
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i06003" }),
            content: intl.formatMessage({ id: "i06005" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: () => deleteByIds([id])
        });
    };

    const onClickActionBarDeleteBtn = async () => {
        if (selectedIds.length === 0 && !isAllSelected) {
            return;
        }
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i06004" }),
            content: intl.formatMessage({ id: "i06005" }),
            cancelText: intl.formatMessage({ id: "b0003" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: () => isAllSelected ? deleteAll() : deleteByIds(selectedIds)
        });
    };

    const onClickDownload = () => {
        if (isAllSelected) {
            synonymRepository.export(agentId, { keyword: pageParam.searchKeyword });
        } else {
            synonymRepository.export(agentId, { ids: selectedIds });
        }
    };

    return (<>
        <div role="table"
             className="selection-table grid-table-synonym"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <IndeterminateCheckbox status={pageSelectionStatus}
                                           onChange={markPage}
                    />
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <SortButton order={pageParam.sortOrder}
                                onClick={() => onClickSortHead(pageParam.sortOrder === "asc" ? "desc" : "asc")}
                    >
                        {intl.formatMessage({ id: "i06017" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i06014" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {page?.content.map((synonym: Synonym) => (
                    <SynonymFragment key={synonym.id}
                                     synonym={synonym}
                                     searchKeyword={pageParam.searchKeyword}
                                     selectedIds={selectedIds}
                                     onCheck={e => markOne(synonym.id, e.target.checked)}
                                     onClickEdit={setEditingSynonym}
                                     onClickDelete={onClickTableRowDeleteBtn}
                    />
                ))}
                {!page?.content?.length && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: pageParam.searchKeyword ? "b0014" : "b0018" })}
                    </div>
                )}
            </div>
        </div>
        <SelectionActionBar selectionCount={selectionCount}
                            selectAll={() => markAll(true)}
                            deSelect={() => markAll(false)}
                            remove={onClickActionBarDeleteBtn}
                            download={onClickDownload}
        />
    </>);
};


interface SynonymFragmentProps {
    synonym: Synonym;
    selectedIds: string[];
    searchKeyword?: string;
    onCheck: (e: React.ChangeEvent<HTMLInputElement>, synonym: Synonym) => void;
    onClickEdit: (synonym: Synonym) => void;
    onClickDelete: (id: string) => void;
}

const SynonymFragment: React.FC<SynonymFragmentProps> = (props) => {
    const { synonym, selectedIds, searchKeyword, onCheck, onClickEdit, onClickDelete } = props;

    const intl = useIntl();

    return (
        <div role="row"
             className="selection-table-tr group"
        >
            <div role="cell"
                 className="selection-table-td"
            >
                <input type="checkbox"
                       className={classNames(
                           selectedIds.length ? "opacity-100" : "opacity-0",
                           "selection-table-checkbox group-interaction"
                       )}
                       checked={selectedIds.includes(synonym.id)}
                       onChange={e => onCheck(e, synonym)}
                />
            </div>
            <div role="cell"
                 className="selection-table-td"
            >
                <HighlightText query={synonym.keyword}
                               keyword={searchKeyword}
                               highlightClassName="text-highlight"
                />
            </div>
            <div role="cell"
                 className="selection-table-td pl-[15px]"
            >
                <HighlightText query={synonym.synonyms.join(", ")}
                               keyword={searchKeyword}
                               highlightClassName="text-highlight"
                />
            </div>
            <div role="cell"
                 className="selection-table-td"
            >
                <Popover>
                    {({ open, close }) => (
                        <>
                            <PopoverButton className="selection-table-popover-btn"
                                           open={open}
                                           close={close}
                            />
                            <PopoverMenu>
                                <PopoverItem className="selection-table-popover-item"
                                            onClick={() => onClickEdit(synonym)}
                                >
                                    <EditIcon className="size-3.5"/>
                                    {intl.formatMessage({ id: "i06002" })}
                                </PopoverItem>
                                <PopoverItem className="selection-table-popover-item text-point-red"
                                            onClick={() => onClickDelete(synonym.id)}
                                >
                                    <DeleteIcon className="size-3.5" />
                                    {intl.formatMessage({ id: "i06003" })}
                                </PopoverItem>
                            </PopoverMenu>
                        </>
                    )}
                </Popover>
            </div>
        </div>
    );
};

export default SynonymTable;
